/*!
 * wh-tracking.js — WorkoutHealthy conversion event tracking
 * Master plan step 0.2. Sends three GA4 events: quote_request_click, phone_click, affiliate_click.
 *
 * No jQuery. No third-party libraries. Loaded with defer.
 * No personally identifiable information is collected or sent.
 *
 * Installed: assets/wh-tracking.js
 * Included once from: layout/theme.liquid
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * CONFIG — edit these values, nothing else.
   * ------------------------------------------------------------------ */

  // Your GA4 Measurement ID, e.g. 'G-ABC1234XYZ'.
  // Leave blank to send events only to an existing gtag / dataLayer on the page.
  // Fill it in and this file will load gtag.js itself, so the events land in GA4
  // even when GA4 runs inside Shopify's sandboxed pixel (where window.gtag does
  // not exist on the page).
  var GA4_MEASUREMENT_ID = 'G-YL9FZYW9Z4';

  // Leave false. Shopify's Google & YouTube app already sends page_view.
  // Setting this true would double-count page views and sessions.
  var SEND_PAGE_VIEW = false;

  // Elements that count as a quote request call to action.
  var QUOTE_SELECTOR = [
    '.wh-quote',
    '[data-wh-quote]',
    'a[href*="request-a-quote"]',
    'a[href*="/pages/quote"]',
    'a[href*="/pages/get-a-quote"]'
  ].join(', ');

  // Hostnames (and any subdomain of them) that count as an affiliate link.
  var AFFILIATE_HOSTS = ['geni.us', 'amzn.to', 'amazon.com'];

  /* ------------------------------------------------------------------ *
   * Guards
   * ------------------------------------------------------------------ */

  // Never track inside the Shopify theme editor. It would pollute real data.
  if (window.Shopify && window.Shopify.designMode) return;

  // Older browsers without closest() get no tracking rather than a thrown error.
  if (!document.documentElement || typeof Element === 'undefined' || !Element.prototype.closest) return;

  /* ------------------------------------------------------------------ *
   * Debug mode
   * Add ?debug_mode=1 to any URL to stream events into GA4 DebugView.
   * The flag sticks for the rest of the browser tab session, so it survives
   * clicking through a theme preview.
   * ------------------------------------------------------------------ */

  var DEBUG = false;
  try {
    if (/[?&]debug_mode=1/.test(window.location.search)) {
      DEBUG = true;
      window.sessionStorage.setItem('wh_debug_mode', '1');
    } else if (window.sessionStorage.getItem('wh_debug_mode') === '1') {
      DEBUG = true;
    }
  } catch (e) {
    // sessionStorage can be blocked. Fall back to the query string only.
    DEBUG = /[?&]debug_mode=1/.test(window.location.search);
  }

  /* ------------------------------------------------------------------ *
   * Transport — gtag if available, dataLayer always.
   * ------------------------------------------------------------------ */

  window.dataLayer = window.dataLayer || [];

  // Load the page-context GA4 library only when a qualifying conversion click
  // occurs. Shopify's Google app already handles page views, so this avoids a
  // duplicate gtag.js download during normal article reading.
  function ensureGtag() {
    if (!GA4_MEASUREMENT_ID || typeof window.gtag === 'function') return;

    window.gtag = function () {
      window.dataLayer.push(arguments);
    };

    var tag = document.createElement('script');
    tag.async = true;
    tag.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(GA4_MEASUREMENT_ID);
    (document.head || document.documentElement).appendChild(tag);

    window.gtag('js', new Date());

    var config = { send_page_view: SEND_PAGE_VIEW };
    if (DEBUG) config.debug_mode = true;
    window.gtag('config', GA4_MEASUREMENT_ID, config);
  }

  function send(eventName, params) {
    var payload = {};
    var key;

    for (key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        if (params[key] !== '' && params[key] != null) payload[key] = params[key];
      }
    }

    if (DEBUG) payload.debug_mode = true;

    // Initialize page-context GA4 only after a real conversion interaction.
    ensureGtag();

    // 1. gtag, when it exists on the page (ours, or one already installed).
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }

    // 2. dataLayer, for Google Tag Manager. Uses a separate object so the
    //    gtag call above is never mutated.
    var dlEvent = { event: eventName };
    for (key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) dlEvent[key] = payload[key];
    }
    window.dataLayer.push(dlEvent);

    if (DEBUG && window.console && window.console.log) {
      window.console.log('[wh-tracking]', eventName, payload);
    }
  }

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  function trim(str) {
    return String(str == null ? '' : str).replace(/^\s+|\s+$/g, '');
  }

  function clean(str, max) {
    // Collapse whitespace and cap length. Keeps params small and readable.
    return trim(String(str == null ? '' : str).replace(/\s+/g, ' ')).slice(0, max || 100);
  }

  function pagePath() {
    // Path only. Query strings are deliberately excluded so nothing that might
    // sit in a URL parameter can leak into analytics.
    return window.location.pathname || '/';
  }

  function hostOf(url) {
    try {
      return new URL(url, window.location.href).hostname.toLowerCase();
    } catch (e) {
      return '';
    }
  }

  function absolute(url) {
    try {
      return new URL(url, window.location.href).href;
    } catch (e) {
      return String(url || '');
    }
  }

  function isAffiliateHost(host) {
    if (!host) return false;
    for (var i = 0; i < AFFILIATE_HOSTS.length; i++) {
      var h = AFFILIATE_HOSTS[i];
      if (host === h || host.slice(-(h.length + 1)) === '.' + h) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * De-duplication
   * A click can bubble through several matching handlers, and some browsers
   * emit both click and auxclick. Fire at most one event per element per
   * event name per 700ms.
   * ------------------------------------------------------------------ */

  var lastFire = { el: null, name: '', at: 0 };

  function isDuplicate(el, name) {
    var now = new Date().getTime();
    if (lastFire.el === el && lastFire.name === name && now - lastFire.at < 700) return true;
    lastFire = { el: el, name: name, at: now };
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Click handling
   * ------------------------------------------------------------------ */

  function handleClick(event) {
    // Ignore right-click. Allow left-click and middle-click (open in new tab).
    if (typeof event.button === 'number' && event.button > 1) return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') {
      target = target && target.parentElement;
      if (!target || typeof target.closest !== 'function') return;
    }

    var link = target.closest('a[href]');
    var href = link ? trim(link.getAttribute('href')) : '';

    /* --- phone_click: tel: links ---------------------------------- */
    if (href && href.slice(0, 4).toLowerCase() === 'tel:') {
      if (isDuplicate(link, 'phone_click')) return;
      send('phone_click', {
        // This is the store's own published number, not a visitor's.
        phone_number: clean(href.slice(4), 32),
        link_text: clean(link.textContent, 100),
        page_path: pagePath()
      });
      return;
    }

    /* --- affiliate_click: outbound geni.us / amzn.to / amazon.com --- */
    if (href) {
      var host = hostOf(href);
      if (isAffiliateHost(host)) {
        if (isDuplicate(link, 'affiliate_click')) return;
        send('affiliate_click', {
          link_url: absolute(href),
          link_domain: host,
          link_text: clean(link.textContent, 100),
          page_path: pagePath(),
          outbound: true
        });
        return;
      }
    }

    /* --- quote_request_click: quote CTAs and .wh-quote ------------- */
    var quote = target.closest(QUOTE_SELECTOR);
    if (quote) {
      if (isDuplicate(quote, 'quote_request_click')) return;
      send('quote_request_click', {
        // Optional labels. Set data-wh-quote="product-page-sticky" on a CTA to
        // tell your quote buttons apart in GA4.
        cta_id: clean(quote.getAttribute('data-wh-quote') || quote.id, 60),
        cta_text: clean(quote.textContent, 100),
        link_url: href ? absolute(href) : '',
        page_path: pagePath()
      });
    }
  }

  // Capture phase, so the event is recorded even when another script calls
  // stopPropagation() or opens a modal on the same click.
  document.addEventListener('click', handleClick, true);
  document.addEventListener('auxclick', handleClick, true);
})();
